// pages/Agents/Workflows/AIAgentConfig.js
import React, { useState } from "react";
import {
  Box, Typography, TextField, FormGroup, FormControlLabel, Checkbox, Chip,
  Card, CardActionArea, CardContent, Collapse, Button,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import WORKFLOW_TEMPLATES, { TEMPLATE_CATEGORIES } from "./workflowTemplates";

export default function AIAgentConfig({
  agentGoal,
  setAgentGoal,
  allowedActions,
  setAllowedActions,
  actions,
}) {
  const [showTemplates, setShowTemplates] = useState(!agentGoal);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateCategory, setTemplateCategory] = useState("surveys");

  // Group actions by category
  const actionsByCategory = actions.reduce((acc, a) => {
    if (!acc[a.category]) acc[a.category] = [];
    acc[a.category].push(a);
    return acc;
  }, {});

  const handleToggleAction = (actionType) => {
    if (allowedActions.includes(actionType)) {
      setAllowedActions(allowedActions.filter((a) => a !== actionType));
    } else {
      setAllowedActions([...allowedActions, actionType]);
    }
  };

  const handleSelectAll = () => {
    if (allowedActions.length === actions.length) {
      setAllowedActions([]);
    } else {
      setAllowedActions(actions.map((a) => a.action_type));
    }
  };

  const handleApplyTemplate = (template) => {
    setAgentGoal(template.agentGoal);
    setAllowedActions(template.allowedActions);
    setSelectedTemplate(template.id);
    setShowTemplates(false);
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        AI Agent Configuration
      </Typography>

      {/* Predefined Templates */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AutoFixHighIcon />}
          endIcon={showTemplates ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          onClick={() => setShowTemplates(!showTemplates)}
          sx={{ mb: 1.5 }}
        >
          {showTemplates ? "Hide Templates" : "Use a Predefined Template"}
        </Button>

        <Collapse in={showTemplates} timeout={300}>
          {/* Resource category selector */}
          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            {TEMPLATE_CATEGORIES.map((cat) => (
              <Chip
                key={cat.key}
                label={`${cat.icon} ${cat.label}`}
                color={templateCategory === cat.key ? "secondary" : "default"}
                variant={templateCategory === cat.key ? "filled" : "outlined"}
                onClick={() => setTemplateCategory(cat.key)}
                clickable
                sx={{ fontWeight: templateCategory === cat.key ? 700 : 400 }}
              />
            ))}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 1.5,
              mb: 2,
            }}
          >
            {WORKFLOW_TEMPLATES
              .filter((tpl) => tpl.category === templateCategory)
              .map((tpl) => (
              <Card
                key={tpl.id}
                variant="outlined"
                sx={{
                  transition: "all 0.25s ease",
                  border: selectedTemplate === tpl.id ? "2px solid" : "1px solid",
                  borderColor: selectedTemplate === tpl.id ? "secondary.main" : "divider",
                  transform: selectedTemplate === tpl.id ? "scale(1.02)" : "scale(1)",
                  "&:hover": {
                    borderColor: "secondary.light",
                    transform: "scale(1.02)",
                    boxShadow: 2,
                  },
                }}
              >
                <CardActionArea onClick={() => handleApplyTemplate(tpl)}>
                  <CardContent sx={{ py: 1.5, px: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {tpl.name}
                      </Typography>
                      {selectedTemplate === tpl.id && (
                        <Chip label="Applied" color="secondary" size="small" />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {tpl.description}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {tpl.allowedActions.map((a) => (
                        <Chip
                          key={a}
                          label={a.replace(".", " ").replace(/_/g, " ")}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: 10, height: 20 }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Collapse>
      </Box>

      <TextField
        label="Agent Goal"
        value={agentGoal}
        onChange={(e) => {
          setAgentGoal(e.target.value);
          if (selectedTemplate) setSelectedTemplate(null);
        }}
        multiline
        rows={3}
        fullWidth
        placeholder="Describe what this agent should accomplish. Example: Create a new volunteer from the input data, assign them to the upcoming event, and send them an email invite."
        sx={{ mb: 3 }}
      />

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Allowed Actions
        </Typography>
        <Chip
          label={allowedActions.length === actions.length ? "Deselect All" : "Select All"}
          onClick={handleSelectAll}
          variant="outlined"
          size="small"
        />
        <Chip
          label={`${allowedActions.length} of ${actions.length} selected`}
          size="small"
          color="primary"
        />
      </Box>

      {Object.entries(actionsByCategory).map(([category, categoryActions]) => (
        <Box key={category} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
            {category}
          </Typography>
          <FormGroup row>
            {categoryActions.map((action) => (
              <FormControlLabel
                key={action.action_type}
                control={
                  <Checkbox
                    checked={allowedActions.includes(action.action_type)}
                    onChange={() => handleToggleAction(action.action_type)}
                    size="small"
                  />
                }
                label={action.label}
                sx={{ minWidth: 220 }}
              />
            ))}
          </FormGroup>
        </Box>
      ))}

      {allowedActions.length === 0 && (
        <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
          No actions selected. The AI agent will have access to all available actions by default.
        </Typography>
      )}
    </Box>
  );
}
